import { type ChangeEvent, Fragment, type ReactElement, useState } from "react"

import { useTranslation } from "react-i18next"
import { useIsMutating } from "react-query"

import { DeleteOutline } from "@mui/icons-material"
import PlayArrowIcon from "@mui/icons-material/PlayArrow"
import { Box, CircularProgress, Grid } from "@mui/material"
import { Button, InputText, NotificationUtils, Typography } from "@synapse-analytics/synapse-ui"
import { type FormikProps, useFormikContext } from "formik"
import type { MRT_ColumnDef } from "material-react-table"
import { v4 as uuidv4 } from "uuid"

import { AccordionHeader } from "../../../components/UI/AccordionHeader"
import { UploadButton } from "../../../components/UI/CustomUpload"
import { ErrorStateWithRefetch } from "../../../components/UI/ErrorStateWithRefetch"
import { MonacoTextEditor } from "../../../components/data-display/MonacoTextEditor"
import { TabularJsonTable } from "../../../components/tables/TabularJsonTable/TabularJsonTable"
import { type MergeOptions, mergeJsonWithArray } from "../../../components/tables/TabularJsonTable/helpers"
import { getTheme } from "../../../hooks/UseTheme"
import "../../../services/KonanAPI"
import { queryClient } from "../../../store/QueryClientStore"
import { readFileContentAsync } from "../../../utils/genericHelpers"
import type {
  CustomScriptTestingProps,
  RequirementsDataFile,
  ScriptEditorProps,
  ScriptTestFeature,
} from "../Interfaces"
import { RequirementsList } from "./RequirementsList"
import { ScriptTestingLogs } from "./ScriptComponents"

import styles from "../Scripts.module.scss"

/**
 * Script editor responsible for showing/editing editor and logs from running tests
 *
 * @param {boolean} isLoading
 * @param {ScriptGroupRetrieve} script
 * @param {boolean} isReadMode
 * @param {FormikProps} formik
 * @param {string[]} consoleLogs
 * @param {boolean} isFetchingFileError
 * @param {string[]} logs
 * @returns {ReactElement}
 */
export function CustomScriptEditor(props: Readonly<ScriptEditorProps>): ReactElement {
  const {
    isLoading,
    script,
    isReadMode,
    formik,
    consoleLogs,
    isFetchingFileError,
    logs,
    isRefetchingFile,
    isConsoleExpanded,
    isLogsExpanded,
    setIsConsoleExpanded,
    setIsLogsExpanded,
    isActionButtonDisabled,
    onActionButtonClick,
    isBuildingState = false,
    canShowDetails = false,
  } = props

  const theme = getTheme()

  const { t } = useTranslation("scripts", {
    keyPrefix: "customScript.components.editor",
  })

  const CURRENT_PY_VERSION = "3.10"

  const isTestScriptMutationLoading = useIsMutating({ mutationKey: ["Test-script-mutation", script?.uuid] })
  const isCancellingScriptMutationLoading = useIsMutating({
    mutationKey: ["cancelBuildingScript", script?.uuid, script?.active_version?.version],
  })

  const onUploadFile = async (e: ChangeEvent<HTMLInputElement>): Promise<void> => {
    e.stopPropagation()
    const file = e.target.files?.[0]
    if (file) {
      try {
        const fileContent = await readFileContentAsync(file)
        formik.setFieldValue("snippet", fileContent)
      } catch {
        NotificationUtils.toast(t("notifications.error"), { severity: "error" })
      }
    }
    e.target.value = ""
  }

  const handleAccordionHeaderClick = (): void => {
    if (!isBuildingState) {
      onActionButtonClick()
      setIsConsoleExpanded()
      setIsLogsExpanded()
    } else {
      onActionButtonClick()
    }
  }

  return (
    <Fragment>
      {isLoading ? (
        <Grid item xs={12} display={"flex"} alignItems={"center"} justifyContent={"center"}>
          <CircularProgress style={{ color: theme.palette.blue.text[2] }} size={36} />
        </Grid>
      ) : (
        <Grid container flexDirection={"column"} gap={0.5} flexWrap={"nowrap"} flex={1}>
          {canShowDetails ? (
            <Grid container item className={`${styles.accordionCodeViewer} ${styles.scriptCodeContainer}`}>
              <Grid item>
                <AccordionHeader
                  view={"code"}
                  isBuildingState={isBuildingState}
                  actionButtonProps={{
                    shouldShow: true,
                    isDisabled: isActionButtonDisabled,
                    onClick: handleAccordionHeaderClick,
                  }}
                  isLoading={isBuildingState ? !!isCancellingScriptMutationLoading : !!isTestScriptMutationLoading}
                  /* TODO: periodically revisit current PY version used in scripts w/DevOps team */
                  title={
                    <Box display="flex" gap={1} alignItems={"center"}>
                      {isBuildingState && (
                        <CircularProgress style={{ color: theme?.palette?.base.text[2] }} size={15} />
                      )}
                      <Typography variantColor={2} variant="p">
                        {t("title", { version: script?.language_version ?? CURRENT_PY_VERSION })}
                      </Typography>
                      {!isReadMode && <UploadButton id="script-file" accept=".py" onChange={onUploadFile} />}
                    </Box>
                  }
                />
              </Grid>

              {(isLoading || isRefetchingFile) && (
                <Grid container item xs={12} className={styles.scriptDialogLoader}>
                  <CircularProgress style={{ color: "rgb(25 118 210)" }} size={36} />
                </Grid>
              )}

              <Grid container className={styles.jsonEditorContainer}>
                {isFetchingFileError && !isRefetchingFile ? (
                  <Grid container item height={"100%"}>
                    <ErrorStateWithRefetch
                      onRefetch={() =>
                        queryClient.fetchQuery(["getFileContent", script?.uuid, script?.active_version.version])
                      }
                      isFetching={isLoading}
                    />
                  </Grid>
                ) : (
                  !isLoading &&
                  !isRefetchingFile && (
                    <MonacoTextEditor
                      key={formik.values.id}
                      value={formik.values.snippet}
                      height="100%"
                      language="python"
                      onChange={(text) => formik.setFieldValue("snippet", text)}
                      enableCopy
                      renderLineHighLight="none"
                      wordWrap="on"
                      width="100%"
                      loading={<CircularProgress style={{ color: "rgb(25 118 210)" }} size={36} />}
                      readOnly={isReadMode}
                      borderRadius="0px 0px 8px 8px"
                    />
                  )
                )}
              </Grid>
            </Grid>
          ) : (
            <Grid className={styles.noPermissionsContainer} container justifyContent="center" alignItems="center">
              <Typography align="center" variant="p" style={{ whiteSpace: "pre-line" }}>
                {t("restrictedMessage")}
              </Typography>
            </Grid>
          )}

          <ScriptTestingLogs
            logs={logs}
            consoleLogs={consoleLogs}
            isTestScriptMutationLoading={!!isTestScriptMutationLoading}
            isConsoleExpanded={isConsoleExpanded}
            isLogsExpanded={!canShowDetails || isLogsExpanded}
            setIsConsoleExpanded={setIsConsoleExpanded}
            setIsLogsExpanded={setIsLogsExpanded}
            canShowDetails={canShowDetails}
          />
        </Grid>
      )}
    </Fragment>
  )
}

/**
 * Requirements packages editor responsible for showing/editing requirements packages used by script
 * while testing
 * @param {FormikProps} formik
 * @param {boolean} isReadMode
 * @param {boolean} isLoading
 * @param {RequirementsDataFile[]} options
 * @param {boolean} isLoadingOptions
 * @param {Function} setRequirementsFile
 * @param {RequirementsDataFile} requirementsFile
 * @returns {ReactElement}
 */
export function CustomRequirementsPackages(
  props: Readonly<{
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    formik: FormikProps<any>
    isReadMode: boolean
    isLoading: boolean
    options: RequirementsDataFile[]
    isLoadingOptions: boolean
    setRequirementsFile: (file: RequirementsDataFile) => void
    requirementsFile: RequirementsDataFile
  }>,
): ReactElement {
  const { formik, isReadMode, isLoading, options, isLoadingOptions, requirementsFile, setRequirementsFile } = props

  const { t } = useTranslation("scripts", {
    keyPrefix: "customScript.components.requirementsPackages",
  })

  const onUploadFile = async (e: ChangeEvent<HTMLInputElement>): Promise<void> => {
    e.stopPropagation()
    const file = e.target.files?.[0]
    if (file) {
      try {
        const fileContent = await readFileContentAsync(file)
        formik.setFieldValue("requirements", fileContent)
      } catch {
        NotificationUtils.toast(t("notifications.error"), { severity: "error" })
      }
    }
    formik.setFieldValue("requirementsFileName", file?.name)
    setRequirementsFile({ label: file?.name })
    e.target.value = ""
  }

  return (
    <Grid container item flexDirection={"column"} className={styles.accordionCodeViewer}>
      <Grid item>
        <AccordionHeader
          view="code"
          /* TODO: periodically revisit current PY version used in scripts w/DevOps team */
          title={
            isReadMode ? (
              (formik.values.requirementsFileName ?? `requirements.txt`)
            ) : (
              <Box display="flex" justifyContent={"space-between"} alignItems={"center"} width={"100%"}>
                <RequirementsList
                  requirements={options}
                  setRequirementsFile={setRequirementsFile}
                  requirementsFile={requirementsFile}
                  isLoading={isLoadingOptions}
                />
                <UploadButton id="requirements-file" accept=".txt" onChange={onUploadFile} />
              </Box>
            )
          }
        />
      </Grid>
      {isLoading ? (
        <Grid container item xs={12} className={styles.scriptDialogLoader}>
          <CircularProgress style={{ color: "rgb(25 118 210)" }} size={36} />
        </Grid>
      ) : (
        <Grid container item xs className={styles.jsonEditorContainer}>
          <MonacoTextEditor
            key={formik.values.id}
            value={isLoading ? "" : formik.values.requirements}
            language="plaintext"
            onChange={(text) => formik.setFieldValue("requirements", text)}
            enableCopy
            renderLineHighLight="none"
            wordWrap="on"
            borderRadius="0px 0px 8px 8px"
            loading={<CircularProgress style={{ color: "rgb(25 118 210)" }} size={36} />}
            readOnly={isReadMode}
          />
        </Grid>
      )}
    </Grid>
  )
}

export function CustomScriptTesting(props: CustomScriptTestingProps): ReactElement {
  const { handleRunTest, isLoading } = props
  const { values, setFieldValue, handleChange } = useFormikContext<{
    testFeatures: Record<string, ScriptTestFeature>
  }>()

  const { t } = useTranslation("scripts", {
    keyPrefix: "customScript.components.testFeatures",
  })

  const rowsPerPage = 5

  const [page, setPage] = useState<number>(0)
  const [search, setSearch] = useState<string | undefined>(undefined)

  type TableDatum = {
    id: string
    name: string
    value: string
  }

  const tableData = Object.values(values.testFeatures)

  const handleRemove = (id: string): void => {
    const newTestFeatures = { ...values.testFeatures }

    delete newTestFeatures[id]

    setFieldValue("testFeatures", newTestFeatures)

    // go back one page automatically if the last page has no more items to show
    Object.keys(newTestFeatures).length <= page * rowsPerPage && setPage(page - 1)
  }

  const handleAddTestFeature = (): void => {
    const id = uuidv4()

    setFieldValue("testFeatures", {
      [id]: {
        id,
        name: "",
        value: "",
      },
      ...tableData,
    })

    setSearch("")
    setPage(0)
  }

  // Define the configuration object for ScriptSchemaFeatureMergeOptions
  const ScriptSchemaFeatureMergeOptions: MergeOptions<TableDatum, undefined> = {
    keyField: "name",
    valueField: "value",
    createNewItem: (key, value) => ({
      id: uuidv4(),
      name: key,
      value: `${value}`,
    }),
  }

  // Create your original function as a simple wrapper
  const convertJsonToTableData = (jsonText: string, existingFeatures: TableDatum[]): TableDatum[] =>
    mergeJsonWithArray(jsonText, existingFeatures, ScriptSchemaFeatureMergeOptions)

  const columns: MRT_ColumnDef<TableDatum>[] = [
    {
      header: t("table.headers.feature"),
      accessorKey: "name",
      Cell: ({ row }) => {
        const value = row.original.name

        return (
          <Grid item xs={12}>
            <InputText
              fullWidth
              hideDescription
              size="small"
              placeholder={t("table.columns.feature.placeholder")}
              id={`testFeatures.${row.original.id}.name`}
              key={row.original.id}
              value={value}
              handleChange={handleChange}
            />
          </Grid>
        )
      },
    },
    {
      header: t("table.headers.value"),
      accessorKey: "value",
      Cell: ({ row }) => {
        return (
          <Grid item xs={12}>
            <InputText
              fullWidth
              hideDescription
              size="small"
              placeholder={t("table.columns.value.placeholder")}
              id={`testFeatures.${row.original.id}.value`}
              key={row.original.id}
              value={row.original.value}
              handleChange={handleChange}
            />
          </Grid>
        )
      },
    },
    {
      header: t("table.headers.action"),
      accessorKey: "action",
      Cell: ({ row }) => {
        return (
          <Grid item xs={12} container flexWrap={"nowrap"}>
            <Button
              variant={"ghost"}
              size="small"
              onClick={() => handleRemove(row.original.id)}
              className={styles.iconButton}
            >
              <DeleteOutline
                style={{
                  fontSize: "16px",
                  fill: "var(--negative-icon-default) !important",
                }}
              />
            </Button>
          </Grid>
        )
      },
    },
  ]

  return (
    <TabularJsonTable
      columns={columns}
      data={tableData}
      setData={(jsonText: string) => {
        setFieldValue(
          "testFeatures",
          convertJsonToTableData(jsonText, tableData).reduce(
            (acc, curr) => {
              return {
                ...acc,
                [curr.id]: curr,
              }
            },
            {} as Record<string, TableDatum>,
          ),
        )
      }}
      headerActions={[
        <Button
          variant="primary-inverse"
          size="small"
          isLoading={isLoading}
          disabled={isLoading}
          onClick={handleRunTest}
          className={styles.runTestButton}
        >
          <PlayArrowIcon style={{ fontSize: 16 }} />
          <Typography variant="a" style={{ color: "var(--primary-inverse-text-default)" }}>
            {t("buttons.runTest")}
          </Typography>
        </Button>,
      ]}
      actions={[
        <Button variant="secondary" size="small" onClick={handleAddTestFeature}>
          {t("buttons.addFeature")}
        </Button>,
      ]}
      rowsPerPage={rowsPerPage}
      page={page}
      setPage={setPage}
      search={search}
      setSearch={setSearch}
    />
  )
}
